import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { ArrowLeft } from '@untitledui/icons';
import { Button } from '@/components/base/buttons/button';
import { quoteService } from '@/data/mock-quotes';
import type { QuoteRequest } from '@/types/quote';
import { useTranslation } from 'react-i18next';
import { QuoteDetailPanel } from '@/components/application/quotes/quote-detail-panel';
import { LoadingIndicator } from '@/components/application/loading-indicator/loading-indicator';

export const FreightForwarderQuoteDetailPage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['pages:quotes', 'common']);
    const [quote, setQuote] = useState<QuoteRequest | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 500));

            if (id) {
                const foundQuote = quoteService.getById(id);
                setQuote(foundQuote || null);
            }

            setIsLoading(false);
        };
        loadData();

        // Handler per aggiornamenti status shipper
        const handleShipperStatusUpdated = (event: CustomEvent) => {
            if (id && event.detail.quoteRequestId === id) {
                const updated = quoteService.getById(id);
                if (updated) {
                    setQuote(updated);
                }
            }
        };

        window.addEventListener('shipperStatusUpdated', handleShipperStatusUpdated as EventListener);

        // Ricarica la quote periodicamente per vedere aggiornamenti
        const intervalId = setInterval(() => {
            if (id) {
                const updated = quoteService.getById(id);
                if (updated) {
                    // Aggiorna solo se ci sono cambiamenti significativi
                    setQuote(prevQuote => {
                        if (!prevQuote) return updated;
                        // Aggiorna se lo status shipper è cambiato o se lastUpdated è più recente
                        if (updated.shipperStatus !== prevQuote.shipperStatus ||
                            (updated.lastUpdated && prevQuote.lastUpdated && updated.lastUpdated > prevQuote.lastUpdated)) {
                            return updated;
                        }
                        return prevQuote;
                    });
                }
            }
        }, 2000); // Manteniamo 2 secondi come backup

        return () => {
            clearInterval(intervalId);
            window.removeEventListener('shipperStatusUpdated', handleShipperStatusUpdated as EventListener);
        };
    }, [id]);

    const handleBack = () => {
        navigate('/freight-forwarder/quotes');
    };

    const handleClosePanel = () => {
        // Non serve più, ma manteniamo per compatibilità con QuoteDetailPanel
        handleBack();
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <LoadingIndicator />
            </div>
        );
    }

    if (!quote) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <p className="text-lg text-tertiary">{t('list.notFound', { ns: 'common' }) || 'Quote non trovata'}</p>
                <Button onClick={handleBack} color="secondary" size="md" iconLeading={ArrowLeft}>
                    {t('buttons.back', { ns: 'common' }) || 'Torna indietro'}
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full -mx-4 -my-6 sm:-mx-6 lg:-mx-8">
            {/* Header with back button */}
            <div className="flex-shrink-0 px-4 py-4 sm:px-6 lg:px-8 border-b border-secondary bg-primary">
                <Button
                    onClick={handleBack}
                    color="secondary"
                    size="md"
                    iconLeading={ArrowLeft}
                >
                    {t('buttons.back', { ns: 'common' }) || 'Torna indietro'}
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
                <QuoteDetailPanel
                    quote={quote}
                    onClose={handleClosePanel}
                />
            </div>
        </div>
    );
};

